// The issues one meeting filed, and what can be done with them.
// One component for both readers:
//
//   mode "owner"  `/focus/meetings/$meetingId` — every action below.
//   mode "link"   the magic-link page — Accept, Edit and Decline only.
//                 Never Restore, never "Erneut versuchen", no failure detail;
//                 "Verfeinern" is whatever `onRefine` does there (sign-in).
//
// One row per issue: the class badge with the deciding quote ("Zusage: …"),
// the title and `#iid`, the status chip from the forge's labels and state
// (Neu / Übernommen / Verworfen / Erledigt), "2 Fragen offen" while open
// questions wait (refine light), the open questions (`MeetingQuestions`,
// folded for the owner, open on the link), the verified quotes folded,
// "Möglicherweise schon #42".
//
// Every action is decided on the server after a fresh read; a click whose
// issue changed meanwhile comes back with the issue as it is now and the page
// shows that (`meetings.error.changed`). Buttons follow the row's status only:
// Accept / Edit / Decline while it is a proposal, Restore while declined.
//
// After "Neu verarbeiten" a new row waits for the owner: "Anlegen" files that
// one row (`onFile`, owner only, `fileable`), after "Entwurf ansehen" shows
// what it files (`draftPreview`: the drafted description, outcome and
// criteria, folded), and a row an earlier revision already filed says
// "Schon angelegt als #N" (`filedAs`) and offers nothing. The owner's row
// also says "Bezug: #N" for the open issues its item reported on
// (`relatedIids`; a status comment posted there names this issue back).
// `readOnly` ("Frühere Auswertung") shows the rows with no action and no
// questions; only "Verfeinern" (a navigation) stays. A "Schon angelegt" row
// of the current revision offers "Ticket aktualisieren" (owner only,
// `updatable`, `MeetingTicketUpdate`: preview, warning, then the write) and,
// once done, "Ticket aktualisiert am …" (`ticketUpdatedAt`).
//
// Owner only, too: a row filed while the doubt card still asks says
// "Rückfrage offen" (`openDoubt`; the chip goes to the card), the issue's
// links read "Hängt ab von #N", "Voraussetzung für #N" and "Verwandt: #N"
// (`links`, as in its body), and an answer that did not reach the issue yet
// says "Antwort noch nicht im Ticket" with "Erneut versuchen"
// (`answerEffect` failed, or pending on a row that is an issue).

import { useState } from "react";

import {
  meetingClock,
  type MeetingIssuePatchRequest,
  type MeetingIssueRowDto,
  type MeetingQuoteDto,
  type MeetingTicketUpdatePreviewDto,
} from "@loom/shared/meetings";
import { stripForgeNeutraliser } from "@loom/shared/issue-body";

import { useFormatDate } from "../../i18n/format";
import { usePlural, useT, type MessageKey } from "../../i18n/index.js";

import { MeetingIssueEdit } from "./MeetingIssueEdit";
import { MeetingQuestions, openQuestionsOf, type QuestionAnswerHandler } from "./MeetingQuestions";
import { MeetingTicketUpdate } from "./MeetingTicketUpdate";
import type { IssueAction } from "./meetingApi";
import { issueChipKeyOf } from "./meetingCopy";

export type ReviewMode = "owner" | "link";

/**
 * Verified quotes, speaker inside Loom only, time as "bei Minute mm:ss". A
 * quote no speaker was matched to has only its chunk's start: no time.
 */
export function QuoteList({ quotes }: { readonly quotes: readonly MeetingQuoteDto[] }) {
  const t = useT();
  return (
    <ul className="meet-quotes">
      {quotes.map((q, i) => (
        <li key={`${q.approxMs}-${i}`} className="meet-quote">
          <q>{q.text}</q>
          <span className="meet-meta">
            {" — "}
            {q.speakerLabel ?? t("meetings.quote.unknownSpeaker")}
            {q.speakerLabel !== null && (
              <>
                {" · "}
                {t("meetings.quote.at", { clock: meetingClock(q.approxMs) })}
              </>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}

const UPDATED_FORMAT: Intl.DateTimeFormatOptions = { dateStyle: "medium", timeStyle: "short" };

/** "#12, #14" */
const refsOf = (iids: readonly number[]): string => iids.map((n) => `#${String(n)}`).join(", ");

/**
 * Has a doubt's answer not reached this row's issue yet? Failed, or still
 * pending on a row that is an issue (one waiting for "Anlegen" takes it when filed).
 */
export const answerNotAppliedOf = (
  row: Pick<MeetingIssueRowDto, "answerEffect" | "state" | "issueIid" | "filedAs">,
): boolean => {
  const effect = row.answerEffect ?? null;
  if (effect === null) return false;
  const isIssue = (row.state === "filed" && row.issueIid !== null) || (row.filedAs ?? null) !== null;
  return effect.state === "failed" || (effect.state === "pending" && isIssue);
};

export interface RowError {
  readonly ticketId: string;
  readonly key: MessageKey;
}

export function MeetingReview({
  mode,
  issues,
  busyTicketId,
  rowError,
  onAction,
  onSaveEdit,
  onRefine,
  onRetry,
  onAnswer,
  onFile,
  onPreviewUpdate,
  onConfirmUpdate,
  onOpenDoubt,
  readOnly = false,
}: {
  readonly mode: ReviewMode;
  readonly issues: readonly MeetingIssueRowDto[];
  /** The row a write is running for; every row's buttons are inert meanwhile. */
  readonly busyTicketId: string | null;
  readonly rowError: RowError | null;
  readonly onAction: (row: MeetingIssueRowDto, action: IssueAction) => void;
  /** Resolves true when the save went through (the form then closes). */
  readonly onSaveEdit: (row: MeetingIssueRowDto, patch: MeetingIssuePatchRequest) => Promise<boolean>;
  /** Absent: no "Verfeinern" (the meeting has no project). */
  readonly onRefine?: (row: MeetingIssueRowDto) => void;
  /** Owner only: "Erneut versuchen" on a row whose filing failed. */
  readonly onRetry?: () => void;
  /** An open question answered (refine light); absent, the questions are not shown. */
  readonly onAnswer?: QuestionAnswerHandler;
  /** Owner only: "Anlegen" on a new row of a reprocess run. */
  readonly onFile?: (row: MeetingIssueRowDto) => void;
  /** Owner only: "Ticket aktualisieren", the preview. */
  readonly onPreviewUpdate?: (row: MeetingIssueRowDto) => Promise<MeetingTicketUpdatePreviewDto>;
  /** Owner only: "Ticket aktualisieren", confirmed; null when it went through, else the line to show. */
  readonly onConfirmUpdate?: (row: MeetingIssueRowDto, currentBodySha256: string) => Promise<MessageKey | null>;
  /** Owner only: "Rückfrage offen" goes to the row's question on the doubt card. */
  readonly onOpenDoubt?: (row: MeetingIssueRowDto) => void;
  /** "Frühere Auswertung": no actions, no questions. */
  readonly readOnly?: boolean;
}) {
  const t = useT();
  const tn = usePlural();
  const updatedWhen = useFormatDate(UPDATED_FORMAT);
  const [editing, setEditing] = useState<string | null>(null);
  const busy = busyTicketId !== null;
  const owner = mode === "owner";

  return (
    <ul className="meet-list">
      {[...issues]
        .sort((a, b) => a.ordinal - b.ordinal)
        .map((row) => {
          const chip = issueChipKeyOf(row);
          const decisive = row.quotes[0];
          const title = row.title === null ? null : stripForgeNeutraliser(row.title);
          const pending = !readOnly && row.state === "filed" && row.issueStatus === "new";
          const filed = row.state === "filed" && row.issueIid !== null;
          // The edit sends the body trio together, so a form that opens
          // without the issue's text would blank it on save. No body (a failed
          // read, or a link DTO that does not carry one yet): no "Bearbeiten".
          const editable = pending && typeof row.body === "string";
          const asked = !readOnly && onAnswer !== undefined && (row.questions ?? []).length > 0;
          const filedAs = row.filedAs ?? null;
          const fileable = !readOnly && owner && onFile !== undefined && row.fileable === true;
          const updatable =
            !readOnly &&
            owner &&
            filedAs !== null &&
            row.updatable === true &&
            onPreviewUpdate !== undefined &&
            onConfirmUpdate !== undefined;
          const updatedAt = owner && filedAs !== null ? (row.ticketUpdatedAt ?? null) : null;
          // Owner only: "Bezug: #N" and, before "Anlegen", the draft it files.
          const related = owner ? (row.relatedIids ?? []) : [];
          const preview = fileable ? (row.draftPreview ?? null) : null;
          const openCount = asked ? openQuestionsOf(row).length : 0;
          // Owner only: the doubt that still asks, the issue's links, an answer not in it yet.
          const doubtOpen = !readOnly && owner && (row.openDoubt ?? null) !== null;
          const links = owner ? (row.links ?? null) : null;
          const notApplied = !readOnly && owner && answerNotAppliedOf(row);
          return (
            <li key={row.ticketId} className="meet-row" data-ticket={row.ticketId}>
              <div className="meet-row-head">
                <span className={`meet-badge is-${row.class}`}>{t(`meetings.class.${row.class}`)}</span>
                <span className="meet-row-title">{title ?? t("meetings.row.untitled")}</span>
                {row.issueIid !== null && <span className="meet-meta">#{row.issueIid}</span>}
                {chip !== null && (owner || row.state === "filed") && (
                  <span className={`focus-chip meet-status is-${row.issueStatus ?? row.state}`}>
                    {t(chip)}
                  </span>
                )}
                {openCount > 0 && (
                  <span className="focus-chip meet-questions-chip">
                    {tn("plural.meetingQuestionsOpen", openCount)}
                  </span>
                )}
                {doubtOpen &&
                  (onOpenDoubt !== undefined ? (
                    <button type="button" className="focus-chip meet-questions-chip" onClick={() => onOpenDoubt(row)}>
                      {t("meetings.issue.openDoubt")}
                    </button>
                  ) : (
                    <span className="focus-chip meet-questions-chip">{t("meetings.issue.openDoubt")}</span>
                  ))}
              </div>
              {decisive !== undefined && (
                <p className="meet-decisive">
                  <span className="meet-decisive-label">{t(`meetings.reason.${row.reason}`)}:</span>{" "}
                  <q>{decisive.text}</q>
                </p>
              )}
              {asked && onAnswer !== undefined && (
                <MeetingQuestions mode={mode} row={row} onAnswer={onAnswer} />
              )}
              {row.quotes.length > 0 && (
                <details className="meet-fold">
                  <summary>
                    {t("meetings.issue.quotes")} ({row.quotes.length})
                  </summary>
                  <QuoteList quotes={row.quotes} />
                </details>
              )}
              {/* Before filing: the draft's question list (the body carries it after). */}
              {!asked && row.openQuestions.length > 0 && (
                <div className="meet-questions">
                  <span className="meet-meta">{t("meetings.issue.openQuestions")}</span>
                  <ul>
                    {row.openQuestions.map((q) => (
                      <li key={q}>{q}</li>
                    ))}
                  </ul>
                </div>
              )}
              {row.existingIid !== null && (
                <p className="meet-meta">{t("meetings.issue.existing", { iid: row.existingIid })}</p>
              )}
              {filedAs !== null && (
                <p className="meet-meta">{t("meetings.issue.filedAs", { iid: filedAs })}</p>
              )}
              {updatedAt !== null && (
                <p className="meet-meta">{t("meetings.update.done", { date: updatedWhen(updatedAt) })}</p>
              )}
              {updatable && onPreviewUpdate !== undefined && onConfirmUpdate !== undefined && (
                <MeetingTicketUpdate
                  row={row}
                  disabled={busy}
                  onPreview={onPreviewUpdate}
                  onConfirm={onConfirmUpdate}
                />
              )}
              {related.length > 0 && (
                <p className="meet-meta">{t("meetings.issue.related", { refs: refsOf(related) })}</p>
              )}
              {links !== null && links.dependsOn.length > 0 && (
                <p className="meet-meta">{t("meetings.issue.dependsOn", { refs: refsOf(links.dependsOn) })}</p>
              )}
              {links !== null && links.neededBy.length > 0 && (
                <p className="meet-meta">{t("meetings.issue.neededBy", { refs: refsOf(links.neededBy) })}</p>
              )}
              {links !== null && links.related.length > 0 && (
                <p className="meet-meta">{t("meetings.issue.relatedLinks", { refs: refsOf(links.related) })}</p>
              )}
              {notApplied && (
                <p className="focus-help" role="status">
                  {t("meetings.issue.answerNotApplied")}
                </p>
              )}
              {preview !== null && (
                <details className="meet-fold meet-draft">
                  <summary>{t("meetings.issue.draft")}</summary>
                  {preview.description.trim() !== "" && (
                    <p className="meet-draft-text">{preview.description}</p>
                  )}
                  {preview.outcome.trim() !== "" && (
                    <p className="meet-draft-text">
                      <span className="meet-meta">{t("meetings.issue.draftOutcome")}:</span>{" "}
                      {preview.outcome}
                    </p>
                  )}
                  {preview.acceptanceCriteria.length > 0 && (
                    <div>
                      <span className="meet-meta">{t("meetings.issue.draftCriteria")}</span>
                      <ul>
                        {preview.acceptanceCriteria.map((c, i) => (
                          <li key={`${String(i)}-${c}`}>{c}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </details>
              )}

              {editing === row.ticketId && editable ? (
                <MeetingIssueEdit
                  row={row}
                  busy={busy}
                  onCancel={() => setEditing(null)}
                  onSave={(patch) => {
                    void onSaveEdit(row, patch).then((ok) => {
                      if (ok) setEditing(null);
                    });
                  }}
                />
              ) : (
                <div className="meet-actions">
                  {pending && (
                    <>
                      <button
                        type="button"
                        className="focus-btn"
                        disabled={busy}
                        onClick={() => onAction(row, "accept")}
                      >
                        {t("meetings.action.accept")}
                      </button>
                      {editable && (
                        <button
                          type="button"
                          className="focus-btn is-ghost"
                          disabled={busy}
                          onClick={() => setEditing(row.ticketId)}
                        >
                          {t("meetings.action.edit")}
                        </button>
                      )}
                      <button
                        type="button"
                        className="focus-btn is-ghost"
                        disabled={busy}
                        onClick={() => onAction(row, "decline")}
                      >
                        {t("meetings.action.decline")}
                      </button>
                    </>
                  )}
                  {fileable && (
                    <button
                      type="button"
                      className="focus-btn"
                      disabled={busy}
                      onClick={() => onFile?.(row)}
                    >
                      {t("meetings.action.file")}
                    </button>
                  )}
                  {!readOnly && owner && row.state === "filed" && row.issueStatus === "declined" && (
                    <button
                      type="button"
                      className="focus-btn is-ghost"
                      disabled={busy}
                      onClick={() => onAction(row, "restore")}
                    >
                      {t("meetings.action.restore")}
                    </button>
                  )}
                  {filed && onRefine !== undefined && row.issueStatus !== "declined" && (
                    <button
                      type="button"
                      className="focus-deeplink"
                      onClick={() => onRefine(row)}
                    >
                      {t("meetings.action.refine")}
                    </button>
                  )}
                  {!readOnly && owner && (row.retryable === true || notApplied) && onRetry !== undefined && (
                    <button
                      type="button"
                      className="focus-btn is-ghost"
                      disabled={busy}
                      onClick={onRetry}
                    >
                      {t("meetings.action.retry")}
                    </button>
                  )}
                </div>
              )}
              {rowError !== null && rowError.ticketId === row.ticketId && (
                <div className="focus-error" role="alert">
                  {t(rowError.key)}
                </div>
              )}
            </li>
          );
        })}
    </ul>
  );
}
